// GENERATED FROM mobile_charts@dcbe681 lib/layout-sanitizer.js
// sha256=cf7c63f7949a8833798279aecc51ef4611475d25a1490519015bc8fe1e3f6712
// DO NOT EDIT HERE. Edit the mobile_charts original and re-run
// scripts/sync-vendored.sh — both products read the SAME saved layouts, so a drawing one strips and the other restores would hang one surface and not the other.
// Layout sanitizer — removes saved drawings the charting library cannot restore,
// so one bad drawing can no longer wedge the chart on the loading spinner.
// mobile_charts is the source of truth; flow-web gets a byte-identical vendored
// copy (scripts/sync-vendored.sh).
//
// HOW A LAYOUT GETS INTO THIS STATE
//
// A line tool serialises `points: deepCopy(this._timePoint)`, and v32's
// LineDataSource._normalizePoints() wipes the whole array when any single anchor
// fails to normalise:  `if (!p.time_t) { e = []; break }`. So an autosave landing
// mid-draw persists `points: []` while style, zorder and ownerSource all look
// perfectly normal.
//
// Coming back in, Pane.restoreLineTool gates on `a.points && _.restorePoints(...)`
// — and `[]` is truthy, so the anchorless tool IS restored and the next view
// update throws. Nothing catches it, so onChartReady never fires.
//
// WHAT IT REMOVES, AND WHY NOTHING ELSE
//
// Every rule was verified against the real v32 library in tools/layout-lab: the
// shape hangs the chart today. Shapes the library merely tolerates are left alone
// even when clearly malformed, because removing them would be a behaviour change
// with no user benefit:
//
//   points key absent        loads fine                       -> left alone
//   anchor price null        loads fine                       -> left alone
//   anchor missing time_t    library drops the drawing itself -> left alone
//   1-of-3 / 2-of-3 anchors  HANGS, but is indistinguishable from a valid short
//                            tool without a per-tool anchor-count table, which
//                            the bundles do not expose. Covered by the watchdog.
//
// Fail-open everywhere. Kill switch: window.__tvSanitizerOff.

// Line tools that can legitimately carry NO anchors: their restore takes the
// positionPercents branch and never reads `points`. An anchored text serialises
// `points: []` as its NORMAL shape, so this exemption is load-bearing — without
// it the sanitizer would delete every anchored note on every load.
//
// isFixed() is defined in exactly three bundles (line-tool-text/-note/-table) as
// `this._properties.childs().anchored.value()`. The Absolute variants default
// `anchored` to true, so state.anchored may be absent and the type must be
// checked too. Re-derive after a library upgrade with:
//   grep -o 'isFixed()\{[^}]*\}' dist/tv_charts/bundles/*.js
const FIXED_CAPABLE_TYPES = [
    'LineToolText',
    'LineToolTextAbsolute',
    'LineToolNote',
    'LineToolNoteAbsolute',
    'LineToolTable',
];

// Superset of the library's isLineToolName set — every name in it starts with
// "LineTool". An unrecognised LineTool* is one the library would assert on anyway.
function isLineToolSource(src) {
    return !!src && typeof src.type === 'string' && src.type.indexOf('LineTool') === 0;
}

function isAnchorlessByDesign(src) {
    if (src.positionPercents !== undefined) { return true; }
    if (src.state && src.state.anchored) { return true; }
    return FIXED_CAPABLE_TYPES.indexOf(src.type) !== -1;
}

// The rule that makes this line tool unrestorable, or null to keep it.
function unrestorableRule(src) {
    // Verified to hang: createLineToolProperties gets undefined for its state arg.
    if (!src.state || typeof src.state !== 'object') { return 'state_missing'; }

    if (isAnchorlessByDesign(src)) { return null; }

    const points = src.points;
    // Absent is fine — the `a.points &&` gate means restorePoints is never called
    // and the tool keeps its constructor defaults. Verified.
    if (points === undefined || points === null) { return null; }
    if (!Array.isArray(points)) { return 'points_not_array'; }
    // The reported corruption. Measured (tools/layout-lab/sanitizer-proof.html):
    // a tool needing 3+ anchors with points:[] never reaches onChartReady, while a
    // 2-anchor trend line is tolerated. Removed in both cases — zero anchors means
    // no geometry to draw, so it is invisible either way, and one uniform rule
    // beats a per-tool anchor-count table we cannot derive from the bundles.
    if (points.length === 0) { return 'points_empty'; }
    for (let i = 0; i < points.length; i++) {
        const p = points[i];
        if (!p || typeof p !== 'object') { return 'anchor_not_object'; }
    }
    return null;
}

/**
 * Sanitize a PARSED inner layout in place. Takes the parsed object so a caller
 * already walking the layout adds no second JSON.parse.
 *
 * @param {object} layout inner TradingView layout root ({ charts: [...] })
 * @returns {Array<{id: *, type: string, rule: string}>} what was removed; an
 *          empty array means the layout was not modified at all.
 */
export function sanitizeLayout(layout) {
    const removed = [];
    if (typeof window !== 'undefined' && window.__tvSanitizerOff) { return removed; }
    if (!layout || !Array.isArray(layout.charts)) { return removed; }
    for (const chart of layout.charts) {
        if (!chart || !Array.isArray(chart.panes)) { continue; }
        for (const pane of chart.panes) {
            if (!pane || !Array.isArray(pane.sources)) { continue; }
            const kept = [];
            for (const src of pane.sources) {
                if (isLineToolSource(src)) {
                    const rule = unrestorableRule(src);
                    if (rule) {
                        removed.push({ id: src.id, type: src.type, rule: rule });
                        continue;
                    }
                }
                kept.push(src);
            }
            // Reassign only when something actually went, so an untouched layout
            // stays byte-identical through the caller's re-stringify check.
            if (kept.length !== pane.sources.length) { pane.sources = kept; }
        }
    }
    return removed;
}

/**
 * Sanitize a stored wrapper string — the shape the backend column holds, whose
 * `content` is the double-encoded inner layout.
 *
 * Fail-open: any parse problem returns the original string with removed = [].
 *
 * @param {string} contentString
 * @returns {{content: string, removed: Array}}
 */
export function sanitizeLayoutWrapper(contentString) {
    if (typeof contentString !== 'string') { return { content: contentString, removed: [] }; }
    try {
        const wrapper = JSON.parse(contentString);
        if (!wrapper || typeof wrapper.content !== 'string') { return { content: contentString, removed: [] }; }
        const layout = JSON.parse(wrapper.content);
        const removed = sanitizeLayout(layout);
        if (removed.length === 0) { return { content: contentString, removed: removed }; }
        wrapper.content = JSON.stringify(layout);
        return { content: JSON.stringify(wrapper), removed: removed };
    } catch (_) {
        return { content: contentString, removed: [] };
    }
}

/**
 * Remove EVERY line tool from a parsed inner layout, in place — drawings-safe
 * mode, used only by the boot watchdog's one recovery attempt for the failures no
 * validation can catch (a short anchor list, a tool chunk that never downloaded).
 * The user gets symbol, interval, indicators, panes and viewport back, minus the
 * drawings, instead of a spinner.
 *
 * The stripped layout must never be SAVED: the caller marks that layout
 * save-blocked for the session, so the drawings stay in the database.
 *
 * @returns {Array<{id: *, type: string, rule: string}>} what was removed
 */
export function stripAllLineTools(layout) {
    const removed = [];
    if (!layout || !Array.isArray(layout.charts)) { return removed; }
    for (const chart of layout.charts) {
        if (!chart || !Array.isArray(chart.panes)) { continue; }
        for (const pane of chart.panes) {
            if (!pane || !Array.isArray(pane.sources)) { continue; }
            const kept = [];
            for (const src of pane.sources) {
                if (isLineToolSource(src)) {
                    removed.push({ id: src.id, type: src.type, rule: 'safe_mode' });
                    continue;
                }
                kept.push(src);
            }
            if (kept.length !== pane.sources.length) { pane.sources = kept; }
        }
    }
    return removed;
}

/** Collapse a removal list into { rule: count } for a telemetry attribute. */
export function summarizeRemovals(removed) {
    const byRule = {};
    for (const r of (removed || [])) {
        byRule[r.rule] = (byRule[r.rule] || 0) + 1;
    }
    return byRule;
}
